import { ArrowUpRight, CheckCircle2, FlaskConical, X } from "lucide-react";
import { useMemo, useState } from "react";

import type { EvaluationResult, GraphSnapshot, TechnologyNode } from "../../domain/model";
import { evaluateNode } from "../../lib/api";
import { CapabilityMark } from "../../components/CapabilityMark";

interface Props {
  node: TechnologyNode;
  snapshot: GraphSnapshot;
  onClose: () => void;
}

export function NodeInspector({ node, snapshot, onClose }: Props) {
  const [evaluation, setEvaluation] = useState<EvaluationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const evidence = useMemo(() => snapshot.evidence.filter((item) => item.nodeIds.includes(node.id)), [snapshot, node.id]);
  const projects = useMemo(() => snapshot.projects.filter((item) => item.nodeIds.includes(node.id)), [snapshot, node.id]);
  const actions = useMemo(() => snapshot.learningActions.filter((item) => item.nodeId === node.id), [snapshot, node.id]);

  async function runEvaluation() {
    setBusy(true);
    try { setEvaluation(await evaluateNode(node.id, evidence.map((item) => item.id))); }
    finally { setBusy(false); }
  }

  return (
    <aside className="node-inspector" aria-label={`${node.name} inspector`}>
      <button className="icon-button inspector-close" onClick={onClose} aria-label="Close inspector"><X /></button>
      <p className="eyebrow">{node.layer} / {node.category}</p>
      <h2>{node.name}</h2>
      <CapabilityMark state={node.capabilityState} />
      <section>
        <h3>What it is</h3><p>{node.summary}</p>
        <h3>Why it exists</h3><p>{node.whyItExists}</p>
      </section>
      <section>
        <h3>System position</h3>
        <div className="tag-list">{node.prerequisiteIds.map((id) => <span key={id}>{snapshot.nodes.find((item) => item.id === id)?.name ?? id}</span>)}</div>
      </section>
      <section>
        <h3>Projects</h3>
        {projects.length ? projects.map((project) => <p key={project.id} className="inspector-row"><strong>{project.name}</strong><span>{project.status}</span></p>) : <p className="empty-state">No linked project.</p>}
      </section>
      <section>
        <h3>Evidence</h3>
        {evidence.length ? evidence.map((item) => (
          <a key={item.id} className="evidence-link" href={item.url} target="_blank" rel="noreferrer">
            <span>{item.type.toUpperCase()} / {item.verification}</span><strong>{item.title}</strong><ArrowUpRight size={15} aria-hidden="true" />
          </a>
        )) : <p className="empty-state">No validated evidence yet.</p>}
      </section>
      <section>
        <h3>Next build</h3>
        {actions.length ? actions.map((item) => <div key={item.id} className="next-build"><span>{item.status}</span><strong>{item.task}</strong><p>{item.reason}</p></div>) : <p className="empty-state">No queued action.</p>}
      </section>
      <button className="command-button full-width" onClick={runEvaluation} disabled={busy}>
        <FlaskConical size={16} aria-hidden="true" /> {busy ? "Evaluating" : "Run evidence gate"}
      </button>
      {evaluation && (
        <div className={evaluation.eligible ? "evaluation-result pass" : "evaluation-result hold"} aria-live="polite">
          <CheckCircle2 size={17} aria-hidden="true" />
          <strong>{evaluation.eligible ? `${evaluation.currentState} -> ${evaluation.recommendation}` : `HOLD / ${evaluation.currentState}`}</strong>
          <p>{evaluation.reasons[0]}</p>
        </div>
      )}
    </aside>
  );
}
